// src/components/reports/ReportSummarizer.js
import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import api from '../../utils/api';
import { format } from 'date-fns';

const ReportSummarizer = () => {
  const { t } = useTranslation();
  const [reports, setReports] = useState([]);
  const [selectedReport, setSelectedReport] = useState(null);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingReports, setLoadingReports] = useState(true);

  useEffect(() => {
    loadReports();
  }, []);

  const loadReports = async () => {
    try {
      setLoadingReports(true);
      const response = await api.get('/api/reports/reports?limit=100');
      setReports(response.data.reports || []);
    } catch (error) {
      console.error('Failed to load reports:', error);
      setReports([]);
    } finally {
      setLoadingReports(false);
    }
  };

  const handleSummarize = async (reportId) => {
    try {
      setLoading(true);
      setSelectedReport(reports.find(r => r.id === reportId));
      const response = await api.post(`/api/journalist/reports/${reportId}/summarize`);
      setSummary(response.data.summary);
    } catch (error) {
      console.error('Failed to summarize report:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loadingReports) {
    return (
      <div className="text-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-shayek-primary dark:border-shayek-red-light mx-auto"></div>
        <p className="mt-2 text-sm text-gray-600 dark:text-shayek-dark-600">{t('common.loading')}</p>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      {/* Reports List */}
      <div>
        <h3 className="text-lg font-semibold text-gray-900 dark:text-shayek-dark-900 mb-4">{t('journalist.summarize.selectReport')}</h3>
        <div className="space-y-3 max-h-[600px] overflow-y-auto">
          {reports.length === 0 ? (
            <p className="text-center text-gray-500 dark:text-shayek-dark-500 py-8">{t('journalist.summarize.noReports')}</p>
          ) : (
            reports.map((report) => (
              <div
                key={report.id}
                onClick={() => handleSummarize(report.id)}
                className={`bg-white dark:bg-shayek-dark-50 border rounded-lg p-4 cursor-pointer transition-colors ${
                  selectedReport?.id === report.id
                    ? 'border-shayek-primary dark:border-shayek-red-light bg-shayek-primary/10 dark:bg-shayek-primary/20'
                    : 'border-gray-200 dark:border-shayek-dark-200 hover:border-shayek-primary dark:hover:border-shayek-red-light hover:bg-gray-50 dark:hover:bg-shayek-dark-100'
                }`}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-gray-900 dark:text-shayek-dark-900 mb-1 line-clamp-2 break-words overflow-hidden">
                      {report.input_preview || report.input_data?.substring(0, 150)}
                    </p>
                    <div className="flex items-center gap-3 text-sm text-gray-600 dark:text-shayek-dark-600 mt-2">
                      <span className="capitalize">{report.category || 'N/A'}</span>
                      <span className="capitalize">{report.status}</span>
                      <span>{format(new Date(report.created_at), 'MMM d, yyyy')}</span>
                    </div>
                  </div>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleSummarize(report.id);
                    }}
                    className="ml-4 px-3 py-1 flex-shrink-0 bg-shayek-primary dark:bg-shayek-red-light text-white rounded-lg hover:bg-shayek-primary-600 dark:hover:bg-shayek-red-dark transition-colors text-sm"
                  >
                    {t('journalist.summarize.summarize')}
                  </button>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Summary Display */}
      <div>
        <h3 className="text-lg font-semibold text-gray-900 dark:text-shayek-dark-900 mb-4">{t('journalist.summarize.summary')}</h3>
        {loading ? (
          <div className="bg-white dark:bg-shayek-dark-50 border border-gray-200 dark:border-shayek-dark-200 rounded-lg p-8 text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-shayek-primary dark:border-shayek-red-light mx-auto mb-4"></div>
            <p className="text-gray-600 dark:text-shayek-dark-600">{t('journalist.summarize.generating')}</p>
          </div>
        ) : summary ? (
          <div className="bg-white dark:bg-shayek-dark-50 border border-gray-200 dark:border-shayek-dark-200 rounded-lg p-6 space-y-6">
            <div>
              <h4 className="font-semibold text-gray-900 dark:text-shayek-dark-900 mb-2">{t('journalist.summarize.basicInfo')}</h4>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-gray-600 dark:text-shayek-dark-600">{t('journalist.summarize.type')}:</span>
                  <span className="ml-2 font-medium capitalize text-gray-900 dark:text-shayek-dark-900">{summary.type}</span>
                </div>
                <div>
                  <span className="text-gray-600 dark:text-shayek-dark-600">{t('journalist.summarize.category')}:</span>
                  <span className="ml-2 font-medium capitalize text-gray-900 dark:text-shayek-dark-900">{summary.category || 'N/A'}</span>
                </div>
                <div>
                  <span className="text-gray-600 dark:text-shayek-dark-600">{t('journalist.summarize.status')}:</span>
                  <span className="ml-2 font-medium capitalize text-gray-900 dark:text-shayek-dark-900">{summary.status}</span>
                </div>
                {summary.word_count && (
                  <div>
                    <span className="text-gray-600 dark:text-shayek-dark-600">{t('journalist.summarize.wordCount')}:</span>
                    <span className="ml-2 font-medium text-gray-900 dark:text-shayek-dark-900">{summary.word_count}</span>
                  </div>
                )}
              </div>
            </div>

            {summary.key_points && summary.key_points.length > 0 && (
              <div>
                <h4 className="font-semibold text-gray-900 dark:text-shayek-dark-900 mb-2">{t('journalist.summarize.keyPoints')}</h4>
                <ul className="list-disc list-inside space-y-1 text-sm text-gray-700 dark:text-shayek-dark-700">
                  {summary.key_points.map((point, index) => (
                    <li key={index}>{point.trim()}</li>
                  ))}
                </ul>
              </div>
            )}

            {summary.ai_summary && (
              <div>
                <h4 className="font-semibold text-gray-900 dark:text-shayek-dark-900 mb-2">{t('journalist.summarize.aiAnalysis')}</h4>
                <p className="text-sm text-gray-700 dark:text-shayek-dark-700">{summary.ai_summary}</p>
              </div>
            )}

            {summary.categories && summary.categories.length > 0 && (
              <div>
                <h4 className="font-semibold text-gray-900 dark:text-shayek-dark-900 mb-2">{t('journalist.summarize.detectedCategories')}</h4>
                <div className="flex flex-wrap gap-2">
                  {summary.categories.map((cat, index) => (
                    <span key={index} className="px-3 py-1 bg-gray-100 dark:bg-shayek-dark-200 text-gray-700 dark:text-shayek-dark-300 rounded-full text-sm">
                      {cat}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {summary.fact_check && (
              <div>
                <h4 className="font-semibold text-gray-900 dark:text-shayek-dark-900 mb-2">{t('journalist.summarize.factCheck')}</h4>
                <div className="bg-gray-50 dark:bg-shayek-dark-100 rounded-lg p-4 space-y-2 text-sm">
                  <div className="flex items-center gap-2">
                    <span className="text-gray-600 dark:text-shayek-dark-600">{t('journalist.summarize.verified')}:</span>
                    <span className={`font-medium ${summary.fact_check.verified ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                      {summary.fact_check.verified ? t('common.yes') : t('common.no')}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-gray-600 dark:text-shayek-dark-600">{t('journalist.summarize.confidence')}:</span>
                    <span className="font-medium text-gray-900 dark:text-shayek-dark-900">{(summary.fact_check.confidence * 100).toFixed(1)}%</span>
                  </div>
                  {summary.fact_check.matches_count > 0 && (
                    <div className="flex items-center gap-2">
                      <span className="text-gray-600 dark:text-shayek-dark-600">{t('journalist.summarize.matches')}:</span>
                      <span className="font-medium text-gray-900 dark:text-shayek-dark-900">{summary.fact_check.matches_count}</span>
                    </div>
                  )}
                </div>
              </div>
            )}

            <button
              onClick={() => {
                setSummary(null);
                setSelectedReport(null);
              }}
              className="w-full px-4 py-2 bg-gray-200 dark:bg-shayek-dark-200 text-gray-700 dark:text-shayek-dark-300 rounded-lg hover:bg-gray-300 dark:hover:bg-shayek-dark-300 transition-colors"
            >
              {t('journalist.summarize.clear')}
            </button>
          </div>
        ) : (
          <div className="bg-white dark:bg-shayek-dark-50 border border-gray-200 dark:border-shayek-dark-200 rounded-lg p-8 text-center text-gray-500 dark:text-shayek-dark-500">
            <p>{t('journalist.summarize.selectReportHint')}</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default ReportSummarizer;

